// hooks/useSlideGenerator.js - WITH WEBCODECS SUPPORT + TOGGLE + IndexedDB Restore
import { useState, useRef, useCallback, useEffect } from 'react';
import { renderSlideToCanvas, selectBestTemplate, downloadSlideAsImage, getTemplatesList } from '../canvas/SlideCanvasRenderer';
import { fetchImagesForSlides } from '../canvas/imageFetcher';
import { useAuth } from '../AuthContext';
import { usePlanTracker } from './usePlanTracker';
import { saveAudioToIndexedDB, getAudioFromIndexedDB } from '../utils/audioStorage';

// ✅ NEW: WebCodecs imports
import { 
  WebCodecsVideoEncoder, 
  isWebCodecsSupported, 
  getSupportedCodec 
} from '../services/webCodecsVideoEncoder';
import { 
  checkWebCodecsSupport, 
  validateEncodingOptions,
  handleEncodingError,
  EncodingPerformanceTracker
} from '../utils/videoEncoderUtils';

// ===== 🔄 TOGGLE: Switch between client/server encoding =====
const USE_WEBCODECS = true; // ✅ Set to false to use RunPod backend
// ============================================================

export const useSlideGenerator = () => {
  const { user } = useAuth();
  const planTracker = usePlanTracker(user?.uid);
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedSlides, setProcessedSlides] = useState([]);
  const [processingError, setProcessingError] = useState(null);
  const [moderationBlock, setModerationBlock] = useState(null);
  const [slideTemplates, setSlideTemplates] = useState({});
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [videoGenerationError, setVideoGenerationError] = useState(null);
  const [videoGenerationProgress, setVideoGenerationProgress] = useState(0);
  const [slideImages, setSlideImages] = useState([]);
  const [isFetchingImages, setIsFetchingImages] = useState(false);
  const [imageFetchError, setImageFetchError] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  
  // ✅ NEW: Store original audio file for WebCodecs
  const [originalAudioFile, setOriginalAudioFile] = useState(null);

  const canvasRef = useRef(null);

  const SERVER_BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';
  const API_BASE_URL = `${SERVER_BASE_URL}/api`;

  // ===== 🆕 RESTORE AUDIO FROM INDEXEDDB ON MOUNT =====
  useEffect(() => {
    const restoreAudioFromIndexedDB = async () => {
      // ✅ ONLY restore if there's NO audio already in React state
      if (originalAudioFile) {
       
        return;
      }

      try {
        const savedAudioId = localStorage.getItem('currentAudioId');
        
        if (savedAudioId) {
         
          
          const audioData = await getAudioFromIndexedDB(savedAudioId);
          
          if (audioData && audioData.file) {
            
            setOriginalAudioFile(audioData.file);
            setAudioUrl(savedAudioId);
            
            // ✅ NEW: Restore slide timings to processedSlides
            if (audioData.slideTimings && audioData.slideTimings.length > 0) {
              
              
              setProcessedSlides(prev => {
                if (prev.length === 0) {
                  
                  return prev;
                }
                
                return prev.map((slide, idx) => ({
                  ...slide,
                  duration: audioData.slideTimings[idx]?.duration || null,
                  startTime: audioData.slideTimings[idx]?.startTime || null,
                  endTime: audioData.slideTimings[idx]?.endTime || null,
                  audioSegments: audioData.slideTimings[idx]?.audioSegments || null
                }));
              });
              
              
            }
          } else {
            
            localStorage.removeItem('currentAudioId');
          }
        }
      } catch (error) {
        
        localStorage.removeItem('currentAudioId');
      }
    };

    restoreAudioFromIndexedDB();
  }, [originalAudioFile]); // Run once on mount

  // ✅ NEW: Restore timings if slides loaded AFTER audio restoration
  useEffect(() => {
    const restoreTimingsToExistingSlides = async () => {
      // Only run if:
      // 1. We have slides
      // 2. We have audio URL
      // 3. Slides don't have timing data yet
      if (
        processedSlides.length > 0 && 
        audioUrl && 
        !processedSlides[0]?.duration
      ) {
        try {
          const savedAudioId = localStorage.getItem('currentAudioId');
          if (savedAudioId) {
            const audioData = await getAudioFromIndexedDB(savedAudioId);
            
            if (audioData?.slideTimings && audioData.slideTimings.length > 0) {
              
              
              setProcessedSlides(prev => prev.map((slide, idx) => ({
                ...slide,
                duration: audioData.slideTimings[idx]?.duration || null,
                startTime: audioData.slideTimings[idx]?.startTime || null,
                endTime: audioData.slideTimings[idx]?.endTime || null,
                audioSegments: audioData.slideTimings[idx]?.audioSegments || null
              })));
              
              
            }
          }
        } catch (error) {
          
        }
      }
    };

    restoreTimingsToExistingSlides();
  }, [processedSlides.length, audioUrl]); // Run when slides or audio changes

  const getFirstValidTemplate = () => {
    const templates = getTemplatesList();
    if (templates.length === 0) {
   
      return null;
    }
    return templates[0].id;
  };

  const validateTemplateId = (templateId) => {
    if (!templateId) return getFirstValidTemplate();
    
    const templates = getTemplatesList();
    const exists = templates.some(t => t.id === templateId);
    
    if (!exists) {
      
      return getFirstValidTemplate();
    }
    
    return templateId;
  };

  const fetchSlideImages = async (slides, topic = '') => {
    setIsFetchingImages(true);
    setImageFetchError(null);

    try {
      
      const images = await fetchImagesForSlides(slides, topic);
      
      setSlideImages(images);
     
      return images;
    } catch (error) {
      
      setImageFetchError(error.message || 'Failed to fetch images');
      return [];
    } finally {
      setIsFetchingImages(false);
    }
  };

 const transcribeAudio = async (audioFile, prompt = '') => {
  
  
  setIsProcessing(true);
  setProcessingError(null);
  setModerationBlock(null);

  try {
    const formData = new FormData();
    formData.append('audio', audioFile);
    formData.append('prompt', prompt);
    formData.append('userId', user?.uid || 'anonymous');

    
    const response = await fetch(`${API_BASE_URL}/transcribe-audio`, {
      method: 'POST',
      body: formData,
    });


    
    const data = await response.json();
    
    

    if (data.blocked) {
   
      setModerationBlock({
        reason: data.reason,
        category: data.category,
        suggestion: data.suggestion,
        type: data.audioContentBlocked ? 'audio' : 'instructions'
      });
      setIsProcessing(false);
      return null;
    }

    if (!response.ok) {
      if (data.limitReached) {
        throw new Error(data.error || 'Monthly limit reached');
      }
      throw new Error(data.error || 'Transcription failed');
    }

    
    setOriginalAudioFile(audioFile);

    // ✅✅✅ GENERATE AUDIO ID ON CLIENT SIDE (can name it whatever you want)
    const clientAudioId = `slideaudio_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    

    // Set audio URL state
    setAudioUrl(clientAudioId);


    // Save to localStorage for persistence across refreshes
    localStorage.setItem('currentAudioId', clientAudioId);


    // Prepare slide timings from backend data
    const slideTimings = data.slides.map(slide => ({
      duration: slide.duration || null,
      startTime: slide.startTime || null,
      endTime: slide.endTime || null,
      audioSegments: slide.audioSegments || null
    }));
    
    
    // Save to IndexedDB with client-generated ID
    saveAudioToIndexedDB(audioFile, clientAudioId, slideTimings).catch(err => {
   
    });
    
    
    
    if (data.hasAudioSync) {
     
    }

      const templatesMap = {};
      data.slides.forEach((slide, index) => {
        const suggestedTemplate = selectBestTemplate(slide.extractedContent || {});
        const validTemplate = validateTemplateId(suggestedTemplate);
        const baseName = validTemplate.replace(/_(left|right|none)$/, '');
        const layoutSuffix = index % 2 === 0 ? '_right' : '_left';
        const alternatingTemplate = validateTemplateId(`${baseName}${layoutSuffix}`);
        templatesMap[index] = alternatingTemplate;
      });
      
      setSlideTemplates(templatesMap);
      setProcessedSlides(data.slides);
      await fetchSlideImages(data.slides, audioFile.name || 'presentation');
      
      return data;

    } catch (err) {
    
      setProcessingError(err.message || 'Failed to transcribe audio');
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  const generateFromPrompt = async (promptText) => {
    setIsProcessing(true);
    setProcessingError(null);
    setModerationBlock(null);

    try {
      const response = await fetch(`${API_BASE_URL}/generate-from-prompt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: promptText,
          userId: user?.uid || 'anonymous'
        })
      });

      const data = await response.json();

      if (data.blocked) {
       
        setModerationBlock({
          reason: data.reason,
          category: data.category,
          suggestion: data.suggestion,
          type: 'prompt'
        });
        setIsProcessing(false);
        return null;
      }

      if (!response.ok) {
        if (data.limitReached) {
          throw new Error(data.error || 'Monthly limit reached');
        }
        throw new Error(data.error || 'Failed to generate slides from prompt');
      }

      const templatesMap = {};
      data.slides.forEach((slide, index) => {
        const suggestedTemplate = selectBestTemplate(slide.extractedContent || {});
        const validTemplate = validateTemplateId(suggestedTemplate);
        const baseName = validTemplate.replace(/_(left|right|none)$/, '');
        const layoutSuffix = index % 2 === 0 ? '_right' : '_left';
        const alternatingTemplate = validateTemplateId(`${baseName}${layoutSuffix}`);
        templatesMap[index] = alternatingTemplate;
      });

      setSlideTemplates(templatesMap);
      setProcessedSlides(data.slides);
      await fetchSlideImages(data.slides, promptText);

      // ===== 🆕 CLEAR AUDIO FOR PROMPT-BASED GENERATIONS =====
      setAudioUrl(null);
      setOriginalAudioFile(null);
      localStorage.removeItem('currentAudioId');

      return data;

    } catch (err) {
      
      setProcessingError(err.message || 'Failed to generate slides from prompt');
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  const getSlideImageUrl = useCallback((slideIndex) => {
    const imageData = slideImages[slideIndex];
    if (imageData && imageData.success && imageData.image && imageData.image.url) {
      return imageData.image.url;
    }
    return null;
  }, [slideImages]);

  // ===== 🆕 CLIENT-SIDE VIDEO GENERATION (WebCodecs) =====
  const generateMP4VideoClient = async (options = {}) => {
    if (!processedSlides || processedSlides.length === 0) {
      throw new Error('No slides to export');
    }

    try {
      

      // Check browser support
      const support = checkWebCodecsSupport();
      if (!support.isSupported) {
      
        throw new Error(support.message);
      }

      // Validate options
      const validation = validateEncodingOptions({
        slides: processedSlides,
        duration: options.duration,
        fps: options.fps,
        audioFile: originalAudioFile,
      });

      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      if (validation.warnings.length > 0) {
        
      }

      setIsGeneratingVideo(true);
      setVideoGenerationError(null);
      setVideoGenerationProgress(0);

      // Create encoder
      const encoder = new WebCodecsVideoEncoder({
        slides: processedSlides,
        slideTemplates: slideTemplates,
        slideImages: slideImages,
        renderSlideToCanvas: renderSlideToCanvas,
        getSlideImageUrl: getSlideImageUrl,
        audioFile: options.includeAudio ? originalAudioFile : null,
        
        transition: options.transition || 'none',
        fps: options.fps || 30,
        duration: options.duration || 3,
        onProgress: (progress) => {
          setVideoGenerationProgress(progress);
        },
        options: options,
      });

      // Start encoding
      const result = await encoder.encode();

 

      // Track usage
      await planTracker.incrementMP4Download();

      return result;

    } catch (error) {
      const errorMessage = handleEncodingError(error);
      
      setVideoGenerationError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsGeneratingVideo(false);
      setVideoGenerationProgress(0);
    }
  };

  // ===== 🔄 SERVER-SIDE VIDEO GENERATION (RunPod) =====
  const generateMP4VideoServer = async (options = {}) => {
    if (!processedSlides || processedSlides.length === 0) {
      throw new Error('No slides to export');
    }

    const limitCheck = planTracker.canDownloadMP4();
    if (!limitCheck.allowed) {
      throw new Error(limitCheck.reason);
    }

    setIsGeneratingVideo(true);
    setVideoGenerationError(null);
    setVideoGenerationProgress(0);

    try {
     
      setVideoGenerationProgress(10);

      const slideImagesData = [];
      const defaultDuration = options.duration || 3;

      for (let i = 0; i < processedSlides.length; i++) {
        const slide = processedSlides[i];
        const templateId = validateTemplateId(slideTemplates[i]);
        const imageUrl = getSlideImageUrl(i);

        const canvas = document.createElement('canvas');
        await renderSlideToCanvas(canvas, slide.extractedContent, templateId, imageUrl);

        const imageData = canvas.toDataURL('image/png');

        let slideDuration;
        if (options.includeAudio && slide.duration) {
          slideDuration = slide.duration;
        } else {
          slideDuration = defaultDuration;
        }

        slideImagesData.push({
          imageData,
          duration: slideDuration,
        });

       

        const progress = 10 + ((i + 1) / processedSlides.length) * 30;
        setVideoGenerationProgress(Math.round(progress));
      }

      setVideoGenerationProgress(40);
      
      const requestBody = {
        slides: slideImagesData,
        transition: options.transition || 'none',
        durations: slideImagesData.map(s => s.duration),
        fps: options.fps || 30,
        userId: user?.uid || 'anonymous'
      };

      if (options.includeAudio && audioUrl) {
        requestBody.audioId = audioUrl;
        
      }

      const response = await fetch(`${API_BASE_URL}/generate-video`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Video generation failed');
      }

      const data = await response.json();
      setVideoGenerationProgress(100);

      await planTracker.incrementMP4Download();
     

      const downloadUrl = `${SERVER_BASE_URL}${data.downloadUrl}`;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return data;

    } catch (err) {
    
      setVideoGenerationError(err.message || 'Failed to generate MP4 video');
      throw err;
    } finally {
      setIsGeneratingVideo(false);
      setVideoGenerationProgress(0);
    }
  };

  // ===== 🎯 SMART ROUTER: Choose client or server based on toggle =====
  const generateMP4Video = async (options = {}) => {
    

    if (USE_WEBCODECS) {
      // Check if WebCodecs is actually supported
      const support = checkWebCodecsSupport();
      
      if (support.isSupported) {

        return await generateMP4VideoClient(options);
      } else {
        
        return await generateMP4VideoServer(options);
      }
    } else {
      
      return await generateMP4VideoServer(options);
    }
  };

  const resetProcessingStates = () => {
    setProcessedSlides([]);
    setProcessingError(null);
    setModerationBlock(null);
    setSlideTemplates({});
    setVideoGenerationError(null);
    setVideoGenerationProgress(0);
    setSlideImages([]);
    setIsFetchingImages(false);
    setImageFetchError(null);
    setOriginalAudioFile(null);
    
    // ===== 🆕 CLEAR LOCALSTORAGE ON RESET =====
    localStorage.removeItem('currentAudioId');
  };

  const handleReset = () => {
    resetProcessingStates();
  };

  const changeSlideTemplate = (slideIndex, templateId) => {
    const validTemplate = validateTemplateId(templateId);
    setSlideTemplates(prev => ({
      ...prev,
      [slideIndex]: validTemplate
    }));
  };

  const downloadSlide = async (slideData, slideIndex) => {
    const canvas = document.createElement('canvas');
    const templateId = validateTemplateId(slideTemplates[slideIndex]);
    const imageUrl = getSlideImageUrl(slideIndex);
    await renderSlideToCanvas(canvas, slideData.extractedContent, templateId, imageUrl);
    downloadSlideAsImage(canvas, slideIndex);
  };

  const copySlideText = async (slideData) => {
    const content = slideData.extractedContent;
    const text = `
${content?.heading || ''}

${content?.subheading || ''}

${content?.bulletPoints?.join('\n') || ''}

${content?.bodyText || ''}
    `.trim();

    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      // Silent fail
    }
  };

  return {
    canvasRef,
    isProcessing,
    processedSlides,
    processingError,
    moderationBlock,
    slideTemplates,
    isGeneratingVideo,
    videoGenerationError,
    videoGenerationProgress,
    slideImages,
    isFetchingImages,
    imageFetchError,
    getSlideImageUrl,
    transcribeAudio,
    generateFromPrompt,
    generateMP4Video, // ✅ Smart router
    fetchSlideImages,
    handleReset,
    changeSlideTemplate,
    downloadSlide,
    copySlideText,
    API_BASE_URL,
    SERVER_BASE_URL,
    setProcessedSlides,
    setSlideTemplates,
    setSlideImages,
    audioUrl,
    setAudioUrl,
    planTracker,
    originalAudioFile, // ✅ NEW: Export for other components
    setOriginalAudioFile, // ✅ NEW: Allow setting from outside
  };
};